import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import useSWR from 'swr'
import { ReloadOutlined } from '@ant-design/icons'
import API from '../../../../utils/api'
import { useToken } from '../../../hooks/auth/token'
import MarketRow from './MarketRow'

const Container = styled.div`
	width: fit-content;
	min-width: 520px;
	max-width: 560px;
	margin-top: 16px;
	background: #fff;
	border-radius: 8px;
	border: 1px solid #e8e8e8;
	overflow: hidden;
`

const Header = styled.div`
	padding: 12px 16px;
	background: #fafafa;
	border-bottom: 1px solid #e8e8e8;
	display: flex;
	align-items: center;
	justify-content: space-between;
`

const Title = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #1a1a2e;
	display: flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
`

const LiveBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 8px;
	background: #f6ffed;
	border: 1px solid #b7eb8f;
	color: #52c41a;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;

	&::before {
		content: '';
		width: 6px;
		height: 6px;
		background: #52c41a;
		border-radius: 50%;
		animation: pulse 1.5s infinite;
	}

	@keyframes pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.4; }
	}
`

const ItemCount = styled.span`
	font-size: 12px;
	color: #8c8c8c;
	font-weight: 400;
`

const RefreshButton = styled.button`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px 10px;
	background: #fff;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	font-size: 12px;
	color: #595959;
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		border-color: #1890ff;
		color: #1890ff;
	}

	&:active {
		background: #f5f5f5;
	}

	svg {
		width: 14px;
		height: 14px;
	}
`

const HeaderRight = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	white-space: nowrap;
`

const UpdateTime = styled.span`
	font-size: 11px;
	color: #bfbfbf;
`

const TableHeader = styled.div`
	display: grid;
	grid-template-columns: 140px 90px 110px;
	padding: 8px 12px;
	background: #fff;
	border-bottom: 1px solid #f0f0f0;
	font-size: 11px;
	font-weight: 500;
	color: #8c8c8c;
`

const TableBody = styled.div`
	max-height: calc(100vh - 280px);
	overflow-y: auto;

	&::-webkit-scrollbar {
		width: 4px;
	}

	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 2px;
	}
`

const EmptyState = styled.div`
	padding: 40px 20px;
	text-align: center;
	color: #8c8c8c;
	font-size: 13px;
`

const LoadingSpinner = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;

	&::after {
		content: '';
		width: 20px;
		height: 20px;
		border: 2px solid #e8e8e8;
		border-top-color: #1890ff;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}

	@keyframes spin {
		to { transform: rotate(360deg); }
	}
`

// 종목 그룹별 색상
const getSymbolColor = (code: string) => {
	const colors: { [key: string]: string } = {
		'NQ': '#6366f1',   // 나스닥 - 보라
		'MNQ': '#8b5cf6',  // 마이크로 나스닥 - 연보라
		'ES': '#3b82f6',   // S&P - 파랑
		'MES': '#60a5fa',  // 마이크로 S&P - 연파랑
		'HSI': '#ef4444',  // 항셍 - 빨강
		'MHI': '#f87171',  // 미니항셍 - 연빨강
		'01': '#10b981',   // 국내선물 - 초록
		'CL': '#f59e0b',   // 원유 - 주황
		'GC': '#eab308',   // 금 - 금색
		'6E': '#06b6d4',   // 유로 - 청록
		'6J': '#ec4899',   // 엔화 - 핑크
	}
	return colors[code] || '#6b7280'
}

const MarketMonitor = () => {
	const { data: token } = useToken()
	const CHART_API = 'https://udf.ever-asset.com'
	const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
	const [isRefreshing, setIsRefreshing] = useState(false)

	// 종목 목록 가져오기
	const { data: exchangePairs, isLoading: isLoadingPairs } = useSWR(
		token ? '/admin/exchangepairs?ov=1' : null,
		async (url) => {
			const response = await API.get(url, token + '', {}, false)
			return response?.result?.result?.list || []
		},
		{ revalidateOnFocus: false }
	)

	// 24H 데이터 가져오기 (Chart API) - 프론트와 동일: 1분마다 체크, 5분마다 실제 API 호출
	const { data: info24HData, mutate: mutateInfo24H } = useSWR(
		'info24h-all',
		async () => {
			const response = await fetch(`${CHART_API}/v1/trade/info24h?symbol=0`)
			const data = await response.json()
			setLastUpdate(new Date())
			return data?.result || []
		},
		{
			revalidateOnFocus: true,
			refreshInterval: 60000, // 1분마다 체크 (프론트와 동일)
			revalidateOnReconnect: true,
		}
	)

	// 수동 새로고침
	const handleRefresh = useCallback(async () => {
		setIsRefreshing(true)
		await mutateInfo24H()
		setTimeout(() => setIsRefreshing(false), 500)
	}, [mutateInfo24H])

	// 시간 포맷팅
	const formatTime = (date: Date) => {
		return date.toLocaleTimeString('ko-KR', {
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		})
	}

	// 배열 확인
	const itemList = Array.isArray(exchangePairs) ? exchangePairs : []
	const info24HList = Array.isArray(info24HData) ? info24HData : []

	// 운영 중인 종목 필터링 (openstatus: 1=운영중, 2=장마감)
	const activeItems = itemList.filter((item: any) =>
		item?.openstatus === 1 || item?.openstatus === 2
	)
	const displayItems = activeItems.length > 0 ? activeItems : itemList

	// 24H 데이터 매칭 함수
	const get24HData = (symbol: string) => {
		return info24HList.find((item: any) => item?.symbol === symbol)
	}

	return (
		<Container>
			<Header>
				<Title>
					실시간 해외선물 시세
					<LiveBadge>LIVE</LiveBadge>
					<ItemCount>{displayItems.length}종목</ItemCount>
				</Title>
				<HeaderRight>
					<UpdateTime>업데이트: {formatTime(lastUpdate)}</UpdateTime>
					<RefreshButton onClick={handleRefresh} disabled={isRefreshing}>
						<ReloadOutlined spin={isRefreshing} />
						새로고침
					</RefreshButton>
				</HeaderRight>
			</Header>
			<TableHeader>
				<div>종목</div>
				<div>현재가</div>
				<div>등락</div>
			</TableHeader>
			<TableBody>
				{isLoadingPairs ? (
					<LoadingSpinner />
				) : displayItems.length > 0 ? (
					displayItems.map((item: any, index: number) => (
						<MarketRow
							key={item.id}
							item={item}
							info24H={get24HData(item.pairs)}
							isEven={index % 2 === 1}
							getSymbolColor={getSymbolColor}
						/>
					))
				) : (
					<EmptyState>운영 중인 종목이 없습니다.</EmptyState>
				)}
			</TableBody>
		</Container>
	)
}

export default React.memo(MarketMonitor)
